import type { ModuleStepKey } from '../../lib/types/setup.js';

/**
 * Phase B per-module Q&A question config — one entry per field the real
 * backend accepts (`apps/api/src/setup/module-answers.ts`'s per-step
 * schema). This file only adds Persian labels/option copy for that same
 * field set — field-by-field, superseded 2026-09-27 by the repo owner's own
 * control-type review of this whole wizard (see module-answers.ts's own
 * updated doc comment).
 *
 * `chip-single` / `chip-multi` render as toggle-button chip groups, for
 * short options that don't need explaining; `radio` renders as a vertical
 * list, each option able to carry a one-line `description` of its
 * consequence; `searchable-select` / `searchable-multi-select` render a
 * filterable dropdown for an option list too long for a chip row;
 * `string-list` renders a small add/remove free-text list (warehouse
 * names). Any field can carry `showIf` to only render once a sibling field
 * already has a given value (e.g. "what kind of previous data?" only after
 * "do you have previous data?" is answered "yes").
 *
 * UI_REFERENCE_SOURCE: Sakhtar Onboarding Setup Desktop.html — see
 * `ModuleAnswersStep.tsx`'s own UI_REFERENCE_SOURCE for the full citation;
 * this file is the per-field data half of that same screen.
 */
/** An 'other' option on a chip/select field pairs with a companion
 * free-text `QuestionField` (`kind: 'text'`) that only renders once that
 * option is selected — its own `key` is what carries the typed note. */
export interface OtherOption {
  readonly value: string;
  readonly noteKey: string;
}

/** Only render this field once the sibling field named `key` (on the same
 * step) currently holds the value `equals`. */
export interface ShowIf {
  readonly key: string;
  readonly equals: string;
}

export type QuestionField = (
  | {
      readonly kind: 'chip-single';
      readonly key: string;
      readonly label: string;
      readonly options: readonly { readonly value: string; readonly label: string }[];
      readonly other?: OtherOption;
    }
  | {
      readonly kind: 'chip-multi';
      readonly key: string;
      readonly label: string;
      readonly options: readonly { readonly value: string; readonly label: string }[];
      readonly other?: OtherOption;
    }
  | { readonly kind: 'boolean'; readonly key: string; readonly label: string }
  | {
      readonly kind: 'string-list';
      readonly key: string;
      readonly label: string;
      readonly placeholder: string;
      /** `maxItems: 1` renders as a single plain text input (no add/remove
       * row chrome) while still writing the same 0-or-1-item string array —
       * for a field that's a repeating list in one mode (multiple
       * warehouses) and a single value in another (one warehouse), without
       * two different keys/schema entries for the same underlying data. */
      readonly maxItems?: number;
    }
  | {
      /** Vertical, one-per-line options with a short description under each
       * — for decisions that need their consequence explained, not just a
       * short scannable label (unlike `chip-single`). */
      readonly kind: 'radio';
      readonly key: string;
      readonly label: string;
      readonly options: readonly {
        readonly value: string;
        readonly label: string;
        readonly description?: string;
      }[];
    }
  | {
      /** Single-select searchable dropdown, for a longer option list than a
       * chip row can hold legibly. */
      readonly kind: 'searchable-select';
      readonly key: string;
      readonly label: string;
      readonly placeholder: string;
      readonly options: readonly { readonly value: string; readonly label: string }[];
      readonly other?: OtherOption;
    }
  | {
      /** Multi-select searchable dropdown (e.g. counting units) — same
       * control, `key` holds a string array. */
      readonly kind: 'searchable-multi-select';
      readonly key: string;
      readonly label: string;
      readonly placeholder: string;
      readonly options: readonly { readonly value: string; readonly label: string }[];
      readonly other?: OtherOption;
    }
) & { readonly showIf?: ShowIf };

export interface ModuleQuestionConfig {
  readonly title: string;
  readonly subtitle: string;
  readonly fields: readonly QuestionField[];
}

/** Phase C's previous-data intent screen — same generic renderer, no answer
 * here ever triggers a real import; it only builds the migration plan a
 * human later acts on (docs/agents/handoffs/onboarding-design-review.md,
 * Part 2 — C4). `dataScope`/`dataSource` only appear once `hasPreviousData`
 * is "yes" (`showIf`). */
export const PREVIOUS_DATA_QUESTION_CONFIG: ModuleQuestionConfig = {
  title: 'اطلاعات قبلی',
  subtitle:
    'این فقط یک سؤال قصد است — پاسخ شما هیچ وارد‌سازی یا انتقال دادهٔ واقعی را آغاز نمی‌کند.',
  fields: [
    {
      kind: 'chip-single',
      key: 'hasPreviousData',
      label: 'داده‌ای از سیستم یا فایل قبلی دارید؟',
      options: [
        { value: 'yes', label: 'بله' },
        { value: 'no', label: 'خیر' },
        { value: 'unsure', label: 'مطمئن نیستم' },
      ],
    },
    {
      kind: 'chip-multi',
      key: 'dataScope',
      label: 'این داده‌ها مربوط به چیست؟',
      options: [
        { value: 'customers', label: 'مشتریان' },
        { value: 'products', label: 'کالاها' },
        { value: 'inventory', label: 'موجودی' },
        { value: 'accounts', label: 'حساب‌ها / مانده‌ها' },
        { value: 'documents', label: 'اسناد' },
        { value: 'projects', label: 'پروژه‌ها' },
      ],
      showIf: { key: 'hasPreviousData', equals: 'yes' },
    },
    {
      kind: 'searchable-select',
      key: 'dataSource',
      label: 'منبع داده چیست؟',
      placeholder: 'جست‌وجو…',
      options: [
        { value: 'excel', label: 'اکسل' },
        { value: 'accounting-software', label: 'نرم‌افزار حسابداری' },
        { value: 'crm', label: 'CRM' },
        { value: 'multiple', label: 'چند نرم‌افزار' },
        { value: 'other', label: 'سایر' },
      ],
      other: { value: 'other', noteKey: 'dataSourceOtherNote' },
      showIf: { key: 'hasPreviousData', equals: 'yes' },
    },
  ],
};

export const MODULE_QUESTION_CONFIG: Record<ModuleStepKey, ModuleQuestionConfig> = {
  'sales-crm-setup': {
    title: 'فروش و CRM',
    subtitle: 'چند سؤال کوتاه دربارهٔ روال فروش شما — هر سؤال اختیاری است.',
    fields: [
      {
        kind: 'chip-single',
        key: 'salesModel',
        label: 'مدل فروش شما چیست؟',
        options: [
          { value: 'b2b', label: 'به کسب‌وکارها' },
          { value: 'b2c', label: 'به مصرف‌کننده' },
          { value: 'both', label: 'هر دو' },
        ],
      },
      {
        kind: 'chip-multi',
        key: 'leadSources',
        label: 'سرنخ‌های فروش معمولاً از کجا می‌آیند؟ (فقط یادداشت می‌شود)',
        options: [
          { value: 'referral', label: 'معرفی' },
          { value: 'social', label: 'اینستاگرام / شبکهٔ اجتماعی' },
          { value: 'website', label: 'وب‌سایت' },
          { value: 'phone', label: 'تماس' },
          { value: 'ads', label: 'تبلیغات' },
          { value: 'in-person', label: 'مراجعهٔ حضوری' },
          { value: 'marketplace', label: 'بازارگاه' },
          { value: 'other', label: 'سایر' },
        ],
        other: { value: 'other', noteKey: 'leadSourcesOtherNote' },
      },
      {
        kind: 'radio',
        key: 'quoteInProcess',
        label: 'از پیش‌فاکتور استفاده می‌کنید؟',
        options: [
          {
            value: 'yes',
            label: 'بله',
            description: 'مسیر پیش‌فاکتور برای شما فعال نمایش داده می‌شود.',
          },
          { value: 'no', label: 'خیر' },
          { value: 'later', label: 'فعلاً نمی‌دانم' },
        ],
      },
      {
        kind: 'chip-multi',
        key: 'orderChannel',
        label: 'سفارش‌ها معمولاً از چه کانالی ثبت می‌شوند؟ (فقط یادداشت می‌شود)',
        options: [
          { value: 'phone', label: 'تلفن' },
          { value: 'in-person', label: 'حضوری' },
          { value: 'whatsapp', label: 'واتساپ / پیام‌رسان' },
          { value: 'instagram', label: 'اینستاگرام' },
          { value: 'website', label: 'سایت' },
          { value: 'salesperson', label: 'ویزیتور' },
        ],
      },
    ],
  },
  'inventory-setup': {
    title: 'انبار',
    subtitle: 'ساختار انبارهای سازمان شما.',
    fields: [
      {
        kind: 'radio',
        key: 'managesInventory',
        label: 'موجودی کالا را در سیستم مدیریت می‌کنید؟',
        options: [
          { value: 'yes', label: 'بله' },
          {
            value: 'no',
            label: 'خیر',
            description:
              'در نسخهٔ فعلی، فروش و خرید کالا به انبار نیاز دارد. انتخاب شما ذخیره می‌شود و بعداً اعمال می‌شود.',
          },
          { value: 'later', label: 'فعلاً نمی‌دانم' },
        ],
      },
      {
        kind: 'chip-single',
        key: 'warehouseModel',
        label: 'یک محل نگهداری دارید یا چند محل؟',
        options: [
          { value: 'single', label: 'یک محل' },
          { value: 'multiple', label: 'چند محل' },
        ],
        showIf: { key: 'managesInventory', equals: 'yes' },
      },
      {
        kind: 'string-list',
        key: 'warehouseNames',
        label: 'نام انبار',
        placeholder: 'مثال: انبار اصلی',
        maxItems: 1,
        showIf: { key: 'warehouseModel', equals: 'single' },
      },
      {
        kind: 'string-list',
        key: 'warehouseNames',
        label: 'نام انبارها',
        placeholder: 'مثال: انبار مرکزی',
        showIf: { key: 'warehouseModel', equals: 'multiple' },
      },
      {
        kind: 'searchable-multi-select',
        key: 'countingUnits',
        label: 'واحدهای شمارش کالا',
        placeholder: 'جست‌وجو…',
        options: [
          { value: 'count', label: 'عدد' },
          { value: 'package', label: 'بسته' },
          { value: 'carton', label: 'کارتن' },
          { value: 'kg', label: 'کیلوگرم' },
          { value: 'g', label: 'گرم' },
          { value: 'metre', label: 'متر' },
          { value: 'sqm', label: 'مترمربع' },
          { value: 'litre', label: 'لیتر' },
          { value: 'other', label: 'سایر' },
        ],
        other: { value: 'other', noteKey: 'countingUnitsOtherNote' },
        showIf: { key: 'managesInventory', equals: 'yes' },
      },
      {
        kind: 'boolean',
        key: 'serialTracking',
        label: 'ردیابی سریالی کالا لازم است؟ (بعداً برای هر کالا هم قابل تنظیم است)',
        showIf: { key: 'managesInventory', equals: 'yes' },
      },
      {
        kind: 'boolean',
        key: 'expiryTracking',
        label: 'ردیابی تاریخ انقضا لازم است؟ (در این نسخه فقط یادداشت می‌شود)',
        showIf: { key: 'managesInventory', equals: 'yes' },
      },
    ],
  },
  'procurement-setup': {
    title: 'خرید و تدارکات',
    subtitle: 'روال درخواست و خرید کالا.',
    fields: [
      {
        kind: 'radio',
        key: 'prRequirement',
        label: 'درخواست خرید داخلی الزامی است؟',
        options: [
          { value: 'always', label: 'همیشه', description: 'هر خرید باید با یک درخواست شروع شود.' },
          { value: 'sometimes', label: 'برای بعضی خریدها' },
          { value: 'never', label: 'فعلاً استفاده نمی‌کنیم' },
        ],
      },
      {
        kind: 'radio',
        key: 'fixedSuppliers',
        label: 'تأمین‌کنندهٔ ثابت دارید؟',
        options: [
          {
            value: 'yes',
            label: 'بله',
            description: 'فقط ذخیره می‌شود؛ فهرست تأمین‌کنندهٔ مجاز در این نسخه نیست.',
          },
          { value: 'no', label: 'خیر' },
        ],
      },
    ],
  },
  'finance-setup': {
    title: 'مالی و حسابداری',
    subtitle:
      'تنظیمات رسمی سال مالی و ارزش افزوده در «تنظیمات پایه سازمان» ثبت شد؛ اینجا فقط یک سؤال دیگر باقی مانده.',
    fields: [
      {
        kind: 'radio',
        key: 'chartOfAccountsChoice',
        label: 'کدینگ حساب‌ها را چطور شروع می‌کنید؟',
        options: [
          {
            value: 'default',
            label: 'کدینگ پیش‌فرض',
            description: 'کدینگ استاندارد گروه / کل / معین همین حالا ساخته می‌شود.',
          },
          {
            value: 'import-own',
            label: 'کدینگ خودم را وارد می‌کنم',
            description: 'کدینگی ساخته نمی‌شود؛ بخش مالی تا ورود کدینگ شما بسته می‌ماند.',
          },
          {
            value: 'decide-later',
            label: 'بعداً تصمیم می‌گیرم',
            description:
              'کدینگی ساخته نمی‌شود و بخش مالی تا انتخاب شما بسته می‌ماند. هر زمان از صفحهٔ حساب‌ها می‌توانید کدینگ پیش‌فرض را انتخاب کنید.',
          },
        ],
      },
    ],
  },
  'projects-setup': {
    title: 'پروژه‌ها و کارها',
    subtitle: 'روال مدیریت کارها در سازمان شما.',
    fields: [
      {
        kind: 'chip-single',
        key: 'taskManagementModel',
        label: 'کارها چگونه مدیریت می‌شوند؟ (فقط یادداشت می‌شود)',
        options: [
          { value: 'within-projects', label: 'بیشتر پروژه‌ای' },
          { value: 'standalone', label: 'بیشتر کارهای روزمره' },
          { value: 'both', label: 'هر دو' },
        ],
      },
    ],
  },
};

/**
 * ERP-TBD-1 — the finance chart question only offers the choices the server
 * says this build accepts (`SetupView.chartOfAccountsChoices`). An older
 * server that does not send the list gets the two always-safe choices.
 */
export function moduleQuestionConfigFor(
  key: ModuleStepKey,
  offeredChartChoices: readonly string[] | undefined,
): ModuleQuestionConfig {
  const config = MODULE_QUESTION_CONFIG[key];
  if (key !== 'finance-setup') return config;
  const offered = offeredChartChoices ?? ['default', 'decide-later'];
  return {
    ...config,
    fields: config.fields.map((field) =>
      field.kind === 'radio' && field.key === 'chartOfAccountsChoice'
        ? { ...field, options: field.options.filter((option) => offered.includes(option.value)) }
        : field,
    ),
  };
}
